import { msg } from "@lingui/macro";
import { MessageDescriptor } from "@lingui/core";
import { locale } from "src/config/app-config";
import { DEFAULT_BASE_CURRENCY, currencyMap } from "src/utils/currencySymbols";
import { Voucher, VoucherCurrency, VoucherStatus } from "src/hooks/useVouchers";

const URGENT_DAYS = 7;
const COUNTDOWN_HOURS = 24;

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

export type StoreCreditVariant = "red" | "green" | "blue";

export type ExpiryState =
  | { kind: "none" }
  | { kind: "date"; date: Date }
  | { kind: "days"; date: Date; days: number }
  | { kind: "countdown"; date: Date };

export const parseExpiry = (value?: string | null): Date | null => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split("-").map(Number);
    const endOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);
    const isRealDate =
      endOfDay.getFullYear() === year &&
      endOfDay.getMonth() === month - 1 &&
      endOfDay.getDate() === day;

    return isRealDate ? endOfDay : null;
  }

  const date = new Date(trimmed.replace(" ", "T"));

  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const getDaysUntil = (expiry: Date, now: Date): number => {
  const diff = startOfDay(expiry).getTime() - startOfDay(now).getTime();

  return Math.max(0, Math.round(diff / MS_PER_DAY));
};

export const isActiveNow = (voucher: Voucher, now: Date): boolean => {
  if (voucher.status?.toLowerCase() !== VoucherStatus.Active) return false;

  const expiry = parseExpiry(voucher.expiryDate);

  return expiry === null || expiry.getTime() > now.getTime();
};

export const hasExpired = (voucher: Voucher, now: Date): boolean => {
  if (voucher.status?.toLowerCase() === VoucherStatus.Expired) return true;

  const expiry = parseExpiry(voucher.expiryDate);

  return expiry !== null && expiry.getTime() <= now.getTime();
};

const compareExpiry = (a: Voucher, b: Voucher, direction: 1 | -1): number => {
  const dateA = parseExpiry(a.expiryDate);
  const dateB = parseExpiry(b.expiryDate);

  if (dateA === null && dateB === null) return 0;
  if (dateA === null) return 1;
  if (dateB === null) return -1;

  return direction * (dateA.getTime() - dateB.getTime());
};

export const sortByExpiry = (a: Voucher, b: Voucher): number =>
  compareExpiry(a, b, 1);

export const sortByLatestExpiry = (a: Voucher, b: Voucher): number =>
  compareExpiry(a, b, -1);

type VoucherBuckets = {
  active: Voucher[];
  history: Voucher[];
};

const HISTORY_STATUSES: string[] = [
  VoucherStatus.Active,
  VoucherStatus.Used,
  VoucherStatus.Expired,
];

export const partitionVouchers = (
  vouchers: (Voucher | null | undefined)[] | null | undefined,
  now: Date
): VoucherBuckets => {
  const active: Voucher[] = [];
  const history: Voucher[] = [];

  (vouchers ?? []).forEach((voucher) => {
    if (!voucher) return;

    if (isActiveNow(voucher, now)) {
      active.push(voucher);
    } else if (HISTORY_STATUSES.includes(voucher.status?.toLowerCase() ?? "")) {
      history.push(voucher);
    }
  });

  return {
    active: active.sort(sortByExpiry),
    history: history.sort(sortByLatestExpiry),
  };
};

export const getExpiryState = (voucher: Voucher, now: Date): ExpiryState => {
  const date = parseExpiry(voucher.expiryDate);
  if (date === null) return { kind: "none" };

  const msLeft = date.getTime() - now.getTime();
  if (msLeft > 0 && msLeft <= COUNTDOWN_HOURS * MS_PER_HOUR) {
    return { kind: "countdown", date };
  }

  const days = getDaysUntil(date, now);
  if (days <= URGENT_DAYS) return { kind: "days", date, days };

  return { kind: "date", date };
};

export const getNextExpiryStateChange = (
  vouchers: Voucher[],
  now: Date
): Date | null => {
  const nextMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1
  ).getTime();

  const changes = vouchers
    .flatMap((voucher) => {
      const expiry = parseExpiry(voucher.expiryDate)?.getTime();
      if (expiry === undefined) return [];

      return [expiry - COUNTDOWN_HOURS * MS_PER_HOUR, expiry, nextMidnight];
    })
    .filter((time) => time > now.getTime());

  return changes.length ? new Date(Math.min(...changes)) : null;
};

export const getVariant = (expiry: ExpiryState): StoreCreditVariant => {
  if (expiry.kind === "none") return "blue";
  if (expiry.kind === "date") return "green";

  return "red";
};

const storeCreditTypeTitles = new Map<string, MessageDescriptor>([
  ["first_order_reward", msg`store_credit_type.first_order_reward`],
  ["app_download", msg`store_credit_type.app_download`],
  ["gift", msg`store_credit_type.gift`],
  ["apology", msg`store_credit_type.apology`],
  ["referral_reward", msg`store_credit_type.referral_reward`],
  ["birthday", msg`store_credit_type.birthday`],
  ["return_refund", msg`store_credit_type.return_refund`],
]);

const WHITESPACE = /\s+/g;

const normaliseType = (type: string): string =>
  type.trim().toLowerCase().replace(WHITESPACE, "_");

export const getStoreCreditTitle = (
  type: Voucher["type"]
): MessageDescriptor | undefined =>
  type ? storeCreditTypeTitles.get(normaliseType(type)) : undefined;

const CURRENCY_CODE = /^[A-Za-z]{3}$/;

const amountFormatters = new Map<string, Intl.NumberFormat>();
const dateFormatters = new Map<string, Intl.DateTimeFormat>();

const getAmountFormatter = (
  formatterLocale: string,
  currencyCode: string,
  fractionDigits: number
): Intl.NumberFormat => {
  const key = `${formatterLocale}:${currencyCode}:${fractionDigits}`;
  let formatter = amountFormatters.get(key);

  if (!formatter) {
    formatter = new Intl.NumberFormat(formatterLocale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    amountFormatters.set(key, formatter);
  }

  return formatter;
};

const getDateFormatter = (formatterLocale: string): Intl.DateTimeFormat => {
  let formatter = dateFormatters.get(formatterLocale);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat(formatterLocale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    dateFormatters.set(formatterLocale, formatter);
  }

  return formatter;
};

export const formatVoucherAmount = (
  amount: number,
  currency: VoucherCurrency | undefined,
  { forceDecimals = false }: { forceDecimals?: boolean } = {},
  formatterLocale: string = locale
): string => {
  const code = currency?.code;
  const currencyCode =
    code && CURRENCY_CODE.test(code)
      ? code.toUpperCase()
      : currencyMap[locale]?.code || DEFAULT_BASE_CURRENCY;
  const fractionDigits = forceDecimals ? 2 : Number.isInteger(amount) ? 0 : 2;

  return getAmountFormatter(
    formatterLocale,
    currencyCode,
    fractionDigits
  ).format(amount);
};

export const formatExpiryDate = (
  date: Date,
  formatterLocale: string = locale
): string => getDateFormatter(formatterLocale).format(date);
